import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBillingBreakdownOptions,
  getBillingConfigOptions,
  listBillingTiersOptions,
  listLedgerEntriesOptions,
  updateBillingConfig,
  createAdjustment,
} from "@harmonyso/billing-api-sdk";
import type {
  BillingBreakdownResponse,
  LedgerPage,
  BillingTierResponse,
  BillingModel,
  OverageInvoicing,
} from "@harmonyso/billing-api-sdk";

export type { BillingBreakdownResponse, LedgerPage, BillingTierResponse };

// Form-friendly billing config update — dates kept as strings
export interface BillingConfigFormData {
  tier_id?: string | null;
  billing_model?: string | null;
  fixed_amount_cents?: number | null;
  free_requests_override?: number | null;
  credits_included_override?: number | null;
  price_per_credit_override?: number | null;
  minimum_commitment_override?: number | null;
  overage_invoicing?: string | null;
  contract_start_date?: string;
  contract_end_date?: string;
}

export function useBillingTiers() {
  return useQuery({
    ...listBillingTiersOptions(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useBillingConfig(tenantId: string | undefined) {
  return useQuery({
    ...getBillingConfigOptions({ path: { tenant_id: tenantId ?? "" } }),
    enabled: !!tenantId,
    retry: (_: number, error: unknown) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404;
    },
  });
}

export function useUpdateBillingConfig(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (form: BillingConfigFormData) => {
      const { data } = await updateBillingConfig({
        path: { tenant_id: tenantId },
        body: {
          tier_id: form.tier_id,
          billing_model: form.billing_model as BillingModel | null | undefined,
          fixed_amount_cents: form.fixed_amount_cents,
          free_requests_override: form.free_requests_override,
          credits_included_override: form.credits_included_override,
          price_per_credit_override: form.price_per_credit_override,
          minimum_commitment_override: form.minimum_commitment_override,
          overage_invoicing: form.overage_invoicing as OverageInvoicing | null | undefined,
          contract_start_date: form.contract_start_date ? new Date(form.contract_start_date) : undefined,
          contract_end_date: form.contract_end_date ? new Date(form.contract_end_date) : undefined,
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ _id: "getBillingConfig" }],
      });
    },
  });
}

export function useBillingBreakdown(tenantId: string | undefined, month?: string) {
  return useQuery({
    ...getBillingBreakdownOptions({
      path: { tenant_id: tenantId ?? "" },
      query: month ? { month } : {},
    }),
    enabled: !!tenantId,
  });
}

export function useBillingBreakdowns(tenantId: string | undefined, months: string[]) {
  return useQueries({
    queries: months.map((month) => ({
      ...getBillingBreakdownOptions({
        path: { tenant_id: tenantId ?? "" },
        query: { month },
      }),
      enabled: !!tenantId,
    })),
  });
}

export function useLedgerEntries(tenantId: string | undefined, month: string, page = 1) {
  const parts = month.split("-");
  const year = Number(parts[0]);
  const mon = Number(parts[1]);
  return useQuery({
    ...listLedgerEntriesOptions({
      path: { tenant_id: tenantId ?? "" },
      query: { year, month: mon, page, page_size: 50 },
    }),
    enabled: !!tenantId,
    retry: (_: number, error: unknown) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      return status !== 404;
    },
  });
}

export function useLedgerEntriesMulti(tenantId: string | undefined, months: string[], page = 1, pageSize = 50) {
  return useQueries({
    queries: months.map((month) => {
      const parts = month.split("-");
      const year = Number(parts[0]);
      const mon = Number(parts[1]);
      return {
        ...listLedgerEntriesOptions({
          path: { tenant_id: tenantId ?? "" },
          query: { year, month: mon, page, page_size: pageSize },
        }),
        enabled: !!tenantId,
        retry: (_: number, error: unknown) => {
          const status = (error as { response?: { status?: number } }).response?.status;
          return status !== 404;
        },
        select: (data: LedgerPage) => ({ month, data }),
      };
    }),
  });
}

export function useCreateAdjustment(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      entry_type: "debit" | "credit";
      amount: number;
      description: string;
      month?: string;
    }) => {
      const { data } = await createAdjustment({
        path: { tenant_id: tenantId },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [{ _id: "listLedgerEntries" }] });
      void queryClient.invalidateQueries({ queryKey: [{ _id: "getBillingBreakdown" }] });
    },
  });
}
